import React, { useEffect, useRef, useState } from "react";
import { Button } from "@progress/kendo-react-buttons";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import FormTextField from "../../components/formFields/FormTextField";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { STATUSARRAY } from "../../_contstants/common";
import { Typography } from "@progress/kendo-react-common";
import {
  createTourService,
  getTourServiceByID,
  tourServiceImageUpload,
  updateTourService,
} from "./services/tourservice.services";
import { clearTourServiceDetails } from "./tourserviceSlice";
import FormCheckbox from "../../components/formFields/FormCheckbox";
import { useLocation, useNavigate } from "react-router-dom";
import ShadowCard from "../../components/common/ShadowCard";
import FormRichTextField from "../../components/formFields/FormRichTextField";
import FormImageUpload from "../../components/formFields/FormImageUpload";

const CreateTourService: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const gridRef = useRef<any>(null);

  const [formKey, setFormKey] = useState(1);

  const TourServiceDetail = useAppSelector(
    (state) => state.tourservice.TourServiceDetail
  );
  const TourServiceID = location.state?.TourServiceID;
  const formLoading = useAppSelector((state) => state.tourservice.formLoading);

  useEffect(() => {
    if (TourServiceID) {
      dispatch(getTourServiceByID(TourServiceID));
    }
  }, [TourServiceID]);

  useEffect(() => {
    return () => {
      dispatch(clearTourServiceDetails());
    };
  }, []);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [TourServiceDetail]);

  const handleSubmit = async (values: any) => {
    const ImageData = new FormData();
    const Image =
      values?.Image && typeof values?.Image !== "string"
        ? values.Image[0].getRawFile()
        : "";
    const ServiceIcon =
      values?.ServiceIcon && typeof values?.ServiceIcon !== "string"
        ? values.ServiceIcon[0].getRawFile()
        : "";
    const ServiceBanner =
      values?.ServiceBanner && typeof values?.ServiceBanner !== "string"
        ? values.ServiceBanner[0].getRawFile()
        : "";

    try {
      if (!TourServiceID) {
        const insertPayload = {
          Title: values.Title ? values.Title : "",
          URL: values.URL ? values.URL : "",
          ShortDescription: values.ShortDescription
            ? values.ShortDescription
            : "",
          LongDescription: values.LongDescription ? values.LongDescription : "",
          IsDisplayHome: values.IsDisplayHome ? values.IsDisplayHome : false,
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(createTourService(insertPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (
            (Image || ServiceIcon || ServiceBanner) &&
            response?.payload?.Data?.ID
          ) {
            ImageData.append("Image", Image);
            ImageData.append("ServiceIcon", ServiceIcon);
            ImageData.append("ServiceBanner", ServiceBanner);
            ImageData.append("ID", response?.payload?.Data?.ID);

            await dispatch(tourServiceImageUpload(ImageData));
          }
          navigate(-1);
        }
      } else {
        const editPayload = {
          ID: TourServiceID,
          Title: values.Title ? values.Title : "",
          URL: values.URL ? values.URL : "",
          ShortDescription: values.ShortDescription
            ? values.ShortDescription
            : "",
          LongDescription: values.LongDescription ? values.LongDescription : "",
          IsDisplayHome: values.IsDisplayHome ? values.IsDisplayHome : false,
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(updateTourService(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (Image || ServiceIcon || ServiceBanner) {
            ImageData.append("Image", Image);
            ImageData.append("ServiceIcon", ServiceIcon);
            ImageData.append("ServiceBanner", ServiceBanner);
            ImageData.append("ID", values?.ID ? values?.ID : "");

            await dispatch(tourServiceImageUpload(ImageData));
          }
          navigate(-1);
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  return (
    <>
      {formLoading && <LoadingPanel gridRef={gridRef} />}
      <Form
        key={formKey}
        onSubmit={handleSubmit}
        initialValues={TourServiceDetail}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement>
            <ShadowCard style={{ padding: 12 }}>
              <GridLayout
                style={{ marginRight: 20 }}
                gap={{ rows: 0, cols: 10 }}
                cols={[
                  { width: "33.33%" },
                  { width: "33.33%" },
                  { width: "33.33%" },
                ]}
              >
                <GridLayoutItem colSpan={3}>
                  <Typography.h4>
                    {TourServiceID ? "Update Service" : "Add Service"}
                  </Typography.h4>
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="Title"
                    label="Title"
                    placeholder="Title"
                    component={FormTextField}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="URL"
                    label="URL"
                    placeholder="URL"
                    component={FormTextField}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    wrapperStyle={{ marginTop: 35 }}
                    name="IsDisplayHome"
                    label="Is Display Home"
                    component={FormCheckbox}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="ShortDescription"
                    label="Short Description"
                    placeholder="Short Description"
                    component={FormRichTextField}
                    wrapperStyle={{
                      width: "100%",
                    }}
                    style={{
                      height: 215,
                    }}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="LongDescription"
                    label="Long Description"
                    placeholder="Long Description"
                    component={FormRichTextField}
                    wrapperStyle={{
                      width: "100%",
                    }}
                    style={{
                      height: 215,
                    }}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="Image"
                    alternateName="Image"
                    type="file"
                    accept="image/*"
                    label="Image"
                    component={FormImageUpload}
                    size={70}
                  />
                  <Field
                    name="ServiceIcon"
                    alternateName="ServiceIcon"
                    type="file"
                    accept="image/*"
                    label="Icon"
                    component={FormImageUpload}
                    size={70}
                  />
                  <Field
                    name="ServiceBanner"
                    alternateName="ServiceBanner"
                    type="file"
                    accept="image/*"
                    label="Banner"
                    component={FormImageUpload}
                    size={70}
                  />
                  <Field
                    name="IsActive"
                    label="Status"
                    astrike={true}
                    placeholder="i.e. Active"
                    component={FormSelectionField}
                    validator={requiredValidator}
                    options={STATUSARRAY?.map((status: any) => {
                      return {
                        value: status?.value,
                        label: status?.label,
                      };
                    })}
                  />
                </GridLayoutItem>
                <GridLayoutItem></GridLayoutItem>
                <GridLayoutItem
                  colSpan={2}
                  style={{
                    marginTop: 20,
                    textAlign: "end",
                  }}
                >
                  <div>
                    <ButtonWithLoading
                      label={TourServiceID ? "Update" : "Save"}
                      type="submit"
                      disabled={!formRenderProps.allowSubmit || formLoading}
                      loading={formLoading}
                    />
                    <Button
                      type="button"
                      fillMode={"outline"}
                      themeColor={"primary"}
                      style={{ marginLeft: 10 }}
                      onClick={() => navigate("/tourservice")}
                    >
                      Cancel
                    </Button>
                  </div>
                </GridLayoutItem>
              </GridLayout>
            </ShadowCard>
          </FormElement>
        )}
      />
    </>
  );
};

export default CreateTourService;
